/**
 * EditBirthDataModal component
 *
 * Modal for editing user's primary birth data.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CityAutocomplete } from './CityAutocomplete';
import type { NatalChart } from '../types';
import type { GeocodeSuggestion } from '../api/charts';

interface EditBirthDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { birth_date: string; birth_time: string; birth_place: string }) => Promise<void>;
  currentData: NatalChart | null;
}

export function EditBirthDataModal({
  isOpen,
  onClose,
  onSave,
  currentData
}: EditBirthDataModalProps) {
  const { t } = useTranslation();
  const [birthDate, setBirthDate] = useState('');
  const [birthTime, setBirthTime] = useState('');
  const [birthPlace, setBirthPlace] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize form with current data
  useEffect(() => {
    if (currentData && isOpen) {
      setBirthDate(currentData.birth_date || '');
      setBirthTime(currentData.birth_time || '');
      setBirthPlace(currentData.birth_place || '');
      setError(null);
    }
  }, [currentData, isOpen]);

  const handleCitySelect = (suggestion: GeocodeSuggestion) => {
    setBirthPlace(suggestion.display_name);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!birthDate || !birthTime || !birthPlace) {
      setError(t('profile.editModal.allFieldsRequired'));
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await onSave({
        birth_date: birthDate,
        birth_time: birthTime,
        birth_place: birthPlace
      });
      onClose();
    } catch (err) {
      console.error('Failed to save birth data:', err);
      setError(t('errors.actionFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="edit-modal-overlay" onClick={onClose}>
      <div className="edit-modal" onClick={e => e.stopPropagation()}>
        <div className="edit-modal-header">
          <h2>{t('profile.editModal.editBirthData')}</h2>
          <button className="edit-modal-close" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="edit-modal-body">
          {error && <div className="edit-modal-error">{error}</div>}

          <div className="edit-form-group">
            <label>{t('profile.birthDateLabel')}</label>
            <input
              type="date"
              value={birthDate}
              onChange={e => setBirthDate(e.target.value)}
              disabled={isSaving}
            />
          </div>

          <div className="edit-form-group">
            <label>{t('profile.birthTimeLabel')}</label>
            <input
              type="time"
              value={birthTime}
              onChange={e => setBirthTime(e.target.value)}
              disabled={isSaving}
            />
          </div>

          <div className="edit-form-group">
            <label>{t('profile.birthPlaceLabel')}</label>
            <CityAutocomplete
              value={birthPlace}
              onChange={setBirthPlace}
              onSelect={handleCitySelect}
              disabled={isSaving}
            />
          </div>

          <div className="edit-modal-actions">
            <button
              type="button"
              className="edit-modal-btn cancel"
              onClick={onClose}
              disabled={isSaving}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="edit-modal-btn save"
              disabled={isSaving}
            >
              {isSaving ? t('common.loading') : t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditBirthDataModal;
